import { useState, useEffect } from 'react';
import { EnhancedUserProfile, EnhancedRecommendationResponse } from '../types';

interface RecommendationState {
  userProfile: EnhancedUserProfile | null;
  recommendations: EnhancedRecommendationResponse | null;
  timestamp: number;
}

const STORAGE_KEY = 'recommendation_state';
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes

export const useRecommendationState = () => {
  const [state, setState] = useState<RecommendationState>({
    userProfile: null,
    recommendations: null,
    timestamp: 0
  });

  // Load state from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsedState: RecommendationState = JSON.parse(stored);
        const isExpired = Date.now() - parsedState.timestamp > CACHE_DURATION;
        
        if (!isExpired) {
          setState(parsedState);
        } else {
          // Clear expired data
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch (error) {
      console.warn('Failed to load recommendation state from localStorage:', error);
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  // Save to localStorage whenever state changes
  const saveState = (userProfile: EnhancedUserProfile, recommendations: EnhancedRecommendationResponse) => {
    const newState: RecommendationState = {
      userProfile,
      recommendations,
      timestamp: Date.now()
    };

    setState(newState);

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
    } catch (error) {
      console.warn('Failed to save recommendation state to localStorage:', error);
    }
  };

  // Clear state
  const clearState = () => {
    setState({
      userProfile: null,
      recommendations: null,
      timestamp: 0
    });

    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn('Failed to clear recommendation state from localStorage:', error);
    }
  };

  // Check if we have valid cached data
  const hasValidCache = () => {
    return state.recommendations !== null && 
           state.userProfile !== null && 
           (Date.now() - state.timestamp) < CACHE_DURATION;
  };

  return {
    userProfile: state.userProfile,
    recommendations: state.recommendations,
    hasValidCache: hasValidCache(),
    saveState,
    clearState
  };
};

export default useRecommendationState;